import {
  DataType,
  ExperienceKeyMetricDto,
  GetExperienceResponseBodyType,
  GraphConfig,
  GraphType,
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
} from "@intelligems/ig-types";
import { shortId } from "@intelligems/ig-utils";
import { DashboardPageName } from "@src/api/types/dashboards";
import { getNewGraphConfig } from "@src/features/analytics/utils";
import { InvertedMetrics } from "@src/types/standard-events";

export const generateShareGraphConfigs = (
  experience: GetExperienceResponseBodyType | undefined,
  pageName: DashboardPageName = "overview"
): GraphConfig[] => {
  if (!experience?.experience) {
    return [];
  }

  // For non-overview pages, use the existing getNewGraphConfig utility
  if (pageName !== "overview" && pageName !== "customMetrics") {
    return getNewGraphConfig(pageName);
  }

  // For custom metrics page
  if (pageName === "customMetrics") {
    const customMetrics = experience.experience.experienceCustomMetrics || [];
    return customMetrics.map((customMetric) => ({
      title: [customMetric.customEvent.name || ""],
      datasetId: "variation_overview" as const,
      isImpactGraph: true,
      graphType: GraphType.Bar,
      metrics: [
        shortId(customMetric.customEvent.id),
        "n_orders",
        "n_visitors",
        "net_revenue",
      ],
      isGroup: false,
      canBeKeyMetric: true,
      hasProbability: true,
      hasTimeseries: true,
      hasValueCI: true,
      hasAudience: true,
      impactMetric: undefined,
      stackedBarLegendTitles: undefined,
      dataTypes: ["percent"] as DataType[],
      hasImpactCalculation: true,
    }));
  }

  // For overview page - generate from key metrics
  if (!experience.experience.experienceKeyMetrics) {
    return [];
  }

  const keyMetrics = experience.experience.experienceKeyMetrics;
  const customMetrics = experience.experience.experienceCustomMetrics || [];

  return keyMetrics.map((keyMetric: ExperienceKeyMetricDto) => {
    const isNetRevenuePerOrder =
      keyMetric?.standardEventId === "netRevenuePerOrder";

    // Look up the custom metric if this key metric uses one
    const customMetric = keyMetric.experienceCustomMetricId
      ? customMetrics.find((cm) => cm.id === keyMetric.experienceCustomMetricId)
      : undefined;

    // Get metric title from custom metric or standard event
    const title = customMetric
      ? customMetric.customEvent.name
      : keyMetric.standardEventId
        ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[keyMetric.standardEventId]
            ?.name || "Unknown Metric"
        : "Unknown Metric";

    return {
      title: [title],
      datasetId: "variation_overview" as const,
      isImpactGraph: true,
      graphType: isNetRevenuePerOrder ? GraphType.StackedBar : GraphType.Bar,
      metrics: isNetRevenuePerOrder
        ? [
            "net_product_revenue_per_order",
            "net_shipping_revenue_per_order",
            "net_revenue_per_order",
          ]
        : (() => {
            // Get analytics metric ID from standard event or custom metric
            const metricId = keyMetric?.standardEventId
              ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[keyMetric.standardEventId]
                  .analyticsMetricId
              : customMetric
                ? shortId(customMetric.customEvent.id)
                : "unknown_metric"; // Fallback for edge case

            return [metricId, "n_orders", "n_visitors", "net_revenue"];
          })(),
      canBeKeyMetric: true,
      hasProbability: true,
      hasTimeseries: true,
      hasValueCI: true,
      hasAudience: true,
      hasImpactCalculation: keyMetric?.standardEventId
        ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[keyMetric.standardEventId]
            .hasImpactCalculation
        : false,
      impactMetric: isNetRevenuePerOrder ? "net_revenue_per_order" : undefined,
      stackedBarLegendTitles: isNetRevenuePerOrder
        ? ["Product AOV", "Shipping AOV"]
        : undefined,
      dataTypes: [
        keyMetric?.standardEventId
          ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[keyMetric.standardEventId]
              .analyticsDataType
          : "percent",
      ] as DataType[],
      impactCalculationColoring:
        keyMetric?.standardEventId &&
        InvertedMetrics.includes(keyMetric.standardEventId)
          ? "inverted"
          : "normal",
    };
  });
};
